import React from 'react';
import { KbSection, Callout, StepList } from '../../components/knowledge/primitives.jsx';
import WizardStepsDemo from '../../components/knowledge/demos/WizardStepsDemo.jsx';
import SeverityCascadeDemo from '../../components/knowledge/demos/SeverityCascadeDemo.jsx';

// "Reporting an event" articles. Everything here requires canWriteCases —
// roles that cannot submit never see this category in the Knowledge TOC.

function WizardBasicsBody() {
  return (
    <>
      <KbSection title="One wizard for every event type">
        <p>
          Every report — adverse event, near miss or clinic incident — starts from
          <strong> New Submission</strong> in the sidebar. The wizard has two steps:
          general information that is the same for every event type, then a details step
          that adapts to the type you pick.
        </p>
        <WizardStepsDemo />
      </KbSection>

      <KbSection title="Step 1 — general info" delay={1}>
        <p>Step 1 identifies where and when the event happened, who was involved, and what type of event it is:</p>
        <StepList steps={[
          <><strong>Clinic</strong> — searchable list of all clinics. If you manage a single clinic it is preselected and read-only.</>,
          <><strong>Date of incident</strong> — when the event happened, not when you are reporting it. The legal timeline is measured from this date.</>,
          <><strong>Staff member</strong> — the team member involved; required on treatment reports (enter N/A if none).</>,
          <><strong>Clinic Manager</strong> — filled automatically from the selected clinic.</>,
          <><strong>Event type</strong> — Treatment, Near Miss or Clinic Incident. This decides what Step 2 asks for.</>,
        ]} />
        <Callout tone="info">
          Fields marked with an asterisk are required — the form won't let you press
          <strong> Continue</strong> or submit until they are filled in.
        </Callout>
      </KbSection>

      <KbSection title="Step 2 — details for your event type" delay={2}>
        <p>
          Step 2 changes with the event type. A <strong>Treatment</strong> report asks for the
          treatment type and a severity code (Code 1 minor to Code 5 critical) and then narrows
          the incident categories to match; for guided treatments a reference rail with example
          photos appears alongside to help you pick the right code. <strong>Near Miss</strong> and
          <strong> Clinic Incident</strong> reports ask for their own shorter field sets instead —
          no severity code at submission.
        </p>
        <p>
          Depending on treatment and severity, some attachments are <em>required</em> — the form
          lists them before you submit, and anything not attached now becomes an outstanding item
          on the case checklist.
        </p>
      </KbSection>

      <KbSection title="What happens when you submit" delay={3}>
        <StepList steps={[
          <>The case is created with an <strong>AE reference</strong> and appears in All Cases immediately.</>,
          <>Required documents that were not attached show as outstanding on the case.</>,
          <>The people responsible are <strong>notified automatically</strong> — you do not need to email anyone.</>,
          <>You can keep working on the case from its detail page: comments, attachments and status updates.</>,
        ]} />
      </KbSection>
    </>
  );
}

function ReportTreatmentBody() {
  return (
    <>
      <KbSection title="Built around the client">
        <p>
          A treatment adverse event is the one report that is about a client, so Step 1
          asks for the <strong>Client name</strong> and <strong>Phone number</strong>
          (both required) where the other event types take an incident title. The{' '}
          <strong>Staff member concerned</strong> is required too — enter N/A if no staff
          member was involved. Step 2 then
          works down a chain of choices — treatment type, subtype where the treatment has
          one, severity code, incident category — with each choice narrowing the next.
        </p>
      </KbSection>

      <KbSection title="Treatment, severity code and category" delay={1}>
        <StepList steps={[
          <><strong>Treatment type</strong> — searchable list of treatments (laser hair removal, injectables, skin treatments…), as configured by your admin.</>,
          <><strong>Subtype</strong> — some treatments branch once more (an injectables treatment into Toxin / Filler, for example); pick the one that applies.</>,
          <><strong>Severity code</strong> — Code 1 to Code 5 in ascending seriousness: Code 1 for mild, expected-range reactions through Code 5 for the most serious events. The exact wording of each code is configured by your admin. Treatments without a code scale skip this field.</>,
          <><strong>Incident category</strong> — required whenever the list has options; it repopulates to show only conditions that match your treatment and code. Pick <strong>Other</strong> and describe the condition if nothing fits.</>,
          <><strong>Review date (Code 3–5)</strong> — for LHR and injectables cases at Code 3, 4 or 5, record the date the client was or will be reviewed (a past date is fine if the review has already happened). Once the review has taken place, update the case by adding a comment.</>,
          <><strong>Consent form (every treatment report)</strong> — captured before you can submit: upload it, or switch on <strong>No consent form</strong> and say why. The reason is recorded on the case as a waiver with your name and the date; a supervisor can lift or set one later.</>,
          <><strong>Laser settings (LHR Code 1–2)</strong> — type them in, or record <strong>No settings recorded</strong>.</>,
          <><strong>Service carried out</strong> — the exact service delivered, e.g. "LHR — full legs · 1064 nm".</>,
        ]} />
        <Callout tone="info">
          Changing the treatment, subtype or code clears the incident category, so you
          always re-pick from the list that matches your final selection.
        </Callout>
        <SeverityCascadeDemo />
      </KbSection>

      <KbSection title="The reference rail" delay={2}>
        <p>
          For guided treatments such as laser hair removal, a reference rail appears beside
          the form on Step 2. It mirrors the printed AE-codes sheet: one card per code with
          its clinical name, an example photo, what the code covers and how it is typically
          caused. Picking a severity code highlights the matching card and scrolls it into
          view, so you can check the case in front of you against the example before you
          commit to a code. The rail closes with a short how-to-proceed checklist —
          reassure the client, take photos, apply aftercare, report to your manager — that
          applies whatever the code.
        </p>
      </KbSection>

      <KbSection title="Required details and documents" delay={3}>
        <p>
          Once treatment and code are picked, a <strong>Required for this code</strong> panel
          may appear (<strong>Required documents</strong> for treatments without codes). It
          lists what this case needs to be <em>complete</em> — not what it needs to submit:
          extra fields (laser settings, a skin type, a serial number…) and typed document
          slots you can fill on the spot. A photo is required for most treatments; higher
          codes ask for more — a therapist statement, say, or laser log files at the top
          end. The panel also flags high-priority codes and who will be involved. The
          training certificate is <em>not</em> asked for at submission any more — the
          training team adds it to the case afterwards, together with the training date
          and trainer name.
        </p>
        <p>
          Injectables cases also offer a few <strong>optional</strong> details on every code
          — product used, area treated, dosage, symptoms, and whether the injector has been
          notified (Yes / No). Record what you know; they never hold up a submission.
        </p>
        <StepList steps={[
          <>Click a slot marked <em>Needed for completion — click to upload</em> to attach that document now. Post photos ask for the date the photo was taken as you attach them.</>,
          <>Anything you cannot attach yet becomes an outstanding item on the case — add it later from the case page.</>,
          <>Finish with the <strong>Incident description</strong>: an objective, factual account of the event, symptoms and immediate actions — no speculation.</>,
        ]} />
      </KbSection>
    </>
  );
}

function ReportNearMissBody() {
  return (
    <>
      <KbSection title="What counts as a near miss">
        <p>
          Nothing happened — but it nearly did. A near miss is a hazard or close call
          caught before anyone was harmed: a blocked fire exit, a trailing cable, expired
          stock spotted at a check. It is about the clinic's facilities and processes, not
          treatment outcomes — if a client actually reacted to a treatment, report a
          <strong> Treatment incident</strong> instead.
        </p>
        <p>
          Because no client is involved, Step 1 asks for a short <strong>Incident
          title</strong> ("Slip on wet floor, reception") instead of a client name, and the
          staff field is optional.
        </p>
      </KbSection>

      <KbSection title="The near-miss form" delay={1}>
        <p>Step 2 asks what nearly happened, why, and what is being done about it:</p>
        <StepList steps={[
          <><strong>Reporting function</strong> — which function is raising this: Compliance, Facilities, Clinical, Operations or Other.</>,
          <><strong>Category</strong> — for example Fire Safety, Needles and Sharps, Infection Control or Laser Safety; pick <strong>Other</strong> and describe it if nothing fits. Your admin may have configured different values.</>,
          <><strong>What nearly went wrong</strong> — an objective, factual account: what was the hazard, what was the close call.</>,
          <><strong>Cause</strong> — why it nearly happened: process, training, equipment, environment.</>,
          <><strong>Corrective action</strong> — what will be done to stop this becoming a real incident.</>,
          <><strong>Assigned to</strong> — who owns that corrective action: the clinic team, clinic manager, facilities or compliance team, or a named person under Other.</>,
          <><strong>Target resolution date</strong> — when the corrective action is due; today or later.</>,
        ]} />
      </KbSection>

      <KbSection title="No severity code" delay={2}>
        <p>
          Near misses do not use the Code 1–5 scale — that rubric is for treatment
          outcomes. Instead there is one optional field, <strong>Potential severity (if it
          had occurred)</strong>: Low, Moderate or High. It calls for a reviewer's
          judgement rather than the reporter's — leave it blank if you are unsure.
        </p>
      </KbSection>

      <KbSection title="After you submit" delay={3}>
        <StepList steps={[
          <>The near miss becomes a case with an <strong>AE reference</strong>, visible in All Cases alongside every other event type.</>,
          <>Your description becomes the case narrative; the category, cause, corrective action, assignee and target date all sit on the case file.</>,
          <>Tracking the corrective action to its target date happens on the case — comments, attachments and status updates, same as any other case.</>,
        ]} />
        <Callout tone="info">
          No uploads are required for a near miss — attach supporting files under
          <strong> Additional attachments</strong> only if you have them.
        </Callout>
      </KbSection>
    </>
  );
}

function ReportRefusalRegisterBody() {
  return (
    <>
      <KbSection title="What the refusal register is for">
        <p>
          When a booking or a procedure is <strong>refused</strong> — most often because
          someone could not provide acceptable ID under the Challenge 25 policy — the
          refusal itself is the record an auditor asks to see. Logging it here builds each
          clinic's refusal register: proof that the request was turned away and that
          nothing was arranged.
        </p>
        <p>
          It is filed as a <strong>Near miss</strong>: nothing happened, because the
          refusal stopped it happening.
        </p>
      </KbSection>

      <KbSection title="Recording a refusal" delay={1}>
        <StepList steps={[
          <>Start a new submission and choose <strong>Near miss</strong>.</>,
          <>Step 1: give it a short <strong>Incident title</strong> ("Refusal — no acceptable ID"), pick the clinic and the date.</>,
          <>Step 2: set <strong>Reporting function</strong> to <strong>Compliance</strong> — the <strong>Refusal Register</strong> category only appears there.</>,
          <>Pick <strong>Refusal Register</strong> as the category. The form changes to the five things the register has to carry.</>,
        ]} />
      </KbSection>

      <KbSection title="The five register fields" delay={2}>
        <StepList steps={[
          <><strong>Date and time of the request</strong> — the exact moment the booking request or enquiry was made. This also becomes the case's date of incident.</>,
          <><strong>Details of the incident</strong> — why the booking or procedure was refused, for example failure to provide acceptable ID under Challenge 25.</>,
          <><strong>Description of the potential buyer</strong> — a brief physical description or identifier, enough to recognise a repeat attempt. Keep it short and factual: no ID numbers, dates of birth or contact details.</>,
          <><strong>Action taken</strong> — confirmation that the appointment was denied and that no arrangements were made.</>,
          <><strong>Staff member who refused the request</strong> — the name or initials of whoever handled it.</>,
        ]} />
        <Callout tone="info">
          Do not attach ID documents or photographs to a refusal entry.
        </Callout>
      </KbSection>

      <KbSection title="What happens when you submit" delay={3}>
        <StepList steps={[
          <>Notifications go out exactly as they do for any other report.</>,
          <>The entry is tagged <strong>RefusalRegister</strong>. The tag is applied by the system and cannot be removed — that is what keeps the register complete.</>,
          <>The case is <strong>closed automatically</strong>. There is no corrective action to chase: refusing the booking was the action.</>,
        ]} />
      </KbSection>

      <KbSection title="Pulling your register for an audit" delay={4}>
        <p>
          Open <strong>All Cases</strong> and pick <strong>RefusalRegister</strong> in the
          Tags filter in the side panel. Because register entries are closed, choosing that
          tag also switches the <strong>Closed</strong> status filter on for you. You only
          ever see your own clinic's cases, so what you get is your clinic's register — add
          a date range for the period the auditor asked about.
        </p>
      </KbSection>
    </>
  );
}

function ReportClinicIncidentBody() {
  return (
    <>
      <KbSection title="When to use Clinic Incident">
        <p>
          A clinic incident is a non-treatment event at a clinic: someone is hurt, or
          property is damaged, in a way unrelated to a clinical treatment. One form covers
          both real-world flavours — an <strong>accident at work</strong> (a staff member
          or contractor injured on the premises) and a <strong>public-liability</strong>
          incident (a member of the public injured) — told apart by the
          <strong> Injured person</strong> field rather than by two separate forms.
        </p>
        <p>
          Like a near miss, Step 1 takes a short <strong>Incident title</strong> instead of
          a client name, and the staff field is optional.
        </p>
      </KbSection>

      <KbSection title="The incident details form" delay={1}>
        <p>Step 2 captures what happened, who was hurt, and what was done at the time:</p>
        <StepList steps={[
          <><strong>Type of incident</strong> — the mechanism: Slip, Trip, Fall, Collision / struck by object, Manual handling, Sharps / needlestick, Exposure to substance, Property / equipment damage — or Other, described in your own words.</>,
          <><strong>Injured person</strong> — Employee, Contractor, Member of public or Other.</>,
          <><strong>What happened</strong> — an objective, factual account: what happened, where, and how.</>,
          <><strong>Nature of injuries</strong> — any injuries sustained.</>,
          <><strong>Immediate response / first aid</strong> — what was done at the time: first aid, area made safe, escalation.</>,
          <><strong>Witness name, contact and statement</strong> — if someone saw it and a statement was taken.</>,
        ]} />
      </KbSection>

      <KbSection title="Names and contact details are optional" delay={2}>
        <p>
          Only the type of incident, the injured-person status and the account of what
          happened are marked required. <strong>Injured person name</strong> and
          <strong> Injured person contact</strong> are captured if known, and the witness
          fields only if applicable — record what you have and add the rest to the case
          later. Clinic incidents have no severity code at submission.
        </p>
      </KbSection>

      <KbSection title="Capture the facts — work the case after" delay={3}>
        <p>
          The wizard captures only the incident facts. Investigation, corrective action,
          follow-up and review are deliberately not submission fields — they happen on the
          case afterwards, through comments, attachments and status updates. Submit with
          what you know now; the case is the place to build the full picture.
        </p>
      </KbSection>
    </>
  );
}

export const REPORTING_ARTICLES = [
  {
    id: 'wizard-basics',
    title: 'Submitting a new case',
    category: 'reporting',
    summary: 'How the two-step New Submission wizard works, from clinic to submit.',
    keywords: ['wizard', 'new case', 'submit', 'report', 'new submission', 'event type'],
    visibleTo: (u) => !!u?.canSubmitCase,
    Body: WizardBasicsBody,
  },
  {
    id: 'report-treatment',
    title: 'Reporting a treatment adverse event',
    category: 'reporting',
    summary: 'Treatment, severity code and category step by step, the reference rail, and the documents each code needs.',
    keywords: ['treatment', 'adverse event', 'severity', 'code', 'incident category', 'reference rail', 'photo', 'uploads', 'lhr'],
    visibleTo: (u) => !!u?.canSubmitCase,
    Body: ReportTreatmentBody,
  },
  {
    id: 'report-near-miss',
    title: 'Reporting a near miss',
    category: 'reporting',
    summary: 'Report a close call — what nearly happened, why, and the corrective action that stops it happening for real.',
    keywords: ['near miss', 'hazard', 'close call', 'corrective action', 'assignee', 'fire safety', 'target date'],
    visibleTo: (u) => !!u?.canSubmitCase,
    Body: ReportNearMissBody,
  },
  {
    id: 'report-refusal-register',
    title: 'Recording a refusal (refusal register)',
    category: 'reporting',
    summary: 'Log a refused booking under Compliance — tagged, closed on submission, and ready to pull for an audit.',
    keywords: ['refusal', 'refusals register', 'refusal register', 'challenge 25', 'id', 'age', 'declined', 'denied', 'compliance', 'audit'],
    visibleTo: (u) => !!u?.canSubmitCase,
    Body: ReportRefusalRegisterBody,
  },
  {
    id: 'report-clinic-incident',
    title: 'Reporting a clinic incident',
    category: 'reporting',
    summary: 'Accidents and injuries unrelated to treatment — capture the facts at submission, work the case afterwards.',
    keywords: ['clinic incident', 'accident', 'injury', 'accident at work', 'public liability', 'witness', 'slip', 'first aid'],
    visibleTo: (u) => !!u?.canSubmitCase,
    Body: ReportClinicIncidentBody,
  },
];
